import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripApi } from '../../api/groupTripApi';
import { travelVipApi } from '../../api/travelVipApi';
import GroupTripDraftForm from './GroupTripDraftForm';
import { groupTripErrorMessage, normalizeGroupTrip, toLocalDateTimePayload, validateGroupTripDraftForm } from './groupTripModel';
import HostCommissionDisclosure from './HostCommissionDisclosure';
import './group-trip.css';

const PUBLICATION_ID_PATTERN = /^[1-9]\d*$/;

export default function CreateGroupTripPage() {
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const publicationId = params.get('publicationId') || '';
  const publicationIdValid = PUBLICATION_ID_PATTERN.test(publicationId);
  const titleId = useId();
  const formId = useId();
  const submitting = useRef(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [capacity, setCapacity] = useState('5');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [visibility, setVisibility] = useState('PUBLIC');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [entitlements, setEntitlements] = useState(null);
  const [vipLoadFailed, setVipLoadFailed] = useState(false);

  useEffect(() => {
    if (!publicationIdValid) return undefined;
    let cancelled = false;
    travelVipApi.getMyEntitlements()
      .then((vip) => { if (!cancelled) { setEntitlements(vip); setVipLoadFailed(false); } })
      .catch(() => { if (!cancelled) { setEntitlements(null); setVipLoadFailed(true); } });
    return () => { cancelled = true; };
  }, [publicationIdValid]);

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting.current) return;
    if (!PUBLICATION_ID_PATTERN.test(publicationId)) {
      setError(t('travel.groupTrip.invalidPublicationId'));
      return;
    }
    const validationError = validateGroupTripDraftForm({
      recruitmentTitle: title,
      summary,
      capacity,
      startAt,
      endAt,
      visibility,
    }, t);
    if (validationError) {
      setError(validationError);
      return;
    }
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      const created = normalizeGroupTrip(await groupTripApi.create({
        journeyPublicationId: publicationId,
        recruitmentTitle: title.trim(),
        summary: summary.trim() || null,
        capacity: Number(capacity),
        displayPrice: null,
        currencyCode: null,
        startAt: toLocalDateTimePayload(startAt),
        endAt: toLocalDateTimePayload(endAt),
        visibility,
      }), t);
      navigate(`/group-trips/${created.groupTripId}`);
    } catch (cause) {
      setError(groupTripErrorMessage(cause, t, 'travel.groupTrip.createFailed'));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  // PD-GT-02: blocking fallback — do not show a fillable form without a valid publicationId
  if (!publicationIdValid) {
    return (
      <main className="group-trips" data-testid="create-group-trip-blocked">
        <header className="group-trips__intro">
          <p>{t('travel.common.host')}</p>
          <h1 id={titleId}>{t('travel.groupTrip.createTitle')}</h1>
        </header>
        <section className="group-trips__state" role="alert">
          <p>請先選擇已發布行程</p>
          <p>你需要先選擇一個自己已公開發布的行程，才能建立揪團。</p>
          <p className="group-trips__hint">草稿與尚未發布的行程不會出現在可選清單。</p>
          <Link
            className="group-trips__link"
            to="/group-trips/picker"
            data-testid="create-group-trip-picker-cta"
          >
            選擇已發布行程
          </Link>
          <Link className="group-trips__link" to="/group-trips">{t('travel.common.back')}</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="group-trips" data-testid="create-group-trip-form">
      <header className="group-trips__intro">
        <p>{t('travel.common.host')}</p>
        <h1 id={titleId}>{t('travel.groupTrip.createTitle')}</h1>
        <span>{t('travel.groupTrip.createCapacityHint')}</span>
        <Link to="/group-trips">{t('travel.common.back')}</Link>
      </header>
      <HostCommissionDisclosure
        loadFailed={vipLoadFailed && !entitlements}
        feeEnabled={Boolean(entitlements?.feeEnabled)}
        vip={Boolean(entitlements?.vip)}
        platformFeeRate={entitlements?.platformFeeRate ?? 0}
        includedInDisplayPrice={entitlements?.includedInDisplayPrice !== false}
        freeTripHint
      />
      <form className="group-trips__panel" id={formId} onSubmit={onSubmit} aria-labelledby={titleId}>
        <GroupTripDraftForm
          formId={formId}
          publicationId={publicationId}
          recruitmentTitle={title}
          onRecruitmentTitleChange={setTitle}
          summary={summary}
          onSummaryChange={setSummary}
          capacity={capacity}
          onCapacityChange={setCapacity}
          startAt={startAt}
          onStartAtChange={setStartAt}
          endAt={endAt}
          onEndAtChange={setEndAt}
          visibility={visibility}
          onVisibilityChange={setVisibility}
          disabled={busy}
          t={t}
        />
        {error ? <p className="group-trips__error" role="alert">{error}</p> : null}
        <button type="submit" disabled={busy || !publicationId} aria-busy={busy}>
          {busy ? t('travel.groupTrip.creating') : t('travel.groupTrip.createDraft')}
        </button>
      </form>
    </main>
  );
}
